import { useCallback, useEffect, useRef, useState } from 'react'

import { TURNSTILE_SITE_KEY } from '../constants/config'

const INIT_MAX_DELAY_MS = 8000
const INIT_RETRY_INTERVAL_MS = 100

export function useTurnstileController (setTurnstileWidgetId) {
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [mountKey, setMountKey] = useState(0)
  const [resetKey, setResetKey] = useState(0)

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null)
    setResetKey((key) => key + 1)
  }, [])

  const destroyTurnstile = useCallback(() => {
    setTurnstileToken(null)
    setTurnstileWidgetId(null)
    setMountKey((key) => key + 1)
  }, [setTurnstileWidgetId])

  return {
    turnstileToken,
    setTurnstileToken,
    mountKey,
    resetKey,
    resetTurnstile,
    destroyTurnstile,
  }
}

export function useTurnstileWidget ({
  containerEl,
  enabled,
  mountKey,
  resetKey,
  setTurnstileToken,
  setTurnstileWidgetId,
}) {
  const widgetIdRef = useRef(null)

  const cleanupWidget = useCallback(() => {
    if (widgetIdRef.current != null && window.turnstile?.remove) {
      window.turnstile.remove(widgetIdRef.current)
    }
    widgetIdRef.current = null
    setTurnstileWidgetId(null)
    setTurnstileToken(null)
  }, [setTurnstileToken, setTurnstileWidgetId])

  useEffect(() => {
    if (!enabled || !containerEl) {
      cleanupWidget()
      return undefined
    }

    let cancelled = false
    let retryTimeoutId = null
    const startedAt = Date.now()

    const scheduleRetry = () => {
      if (cancelled || widgetIdRef.current != null) return
      if (Date.now() - startedAt > INIT_MAX_DELAY_MS) return
      retryTimeoutId = window.setTimeout(tryRender, INIT_RETRY_INTERVAL_MS)
    }

    const tryRender = () => {
      if (cancelled || widgetIdRef.current != null) return

      const container = containerEl
      if (!window?.turnstile?.render || !container.isConnected || container.offsetWidth === 0) {
        scheduleRetry()
        return
      }

      container.innerHTML = ''

      window.turnstile.ready(() => {
        if (cancelled || widgetIdRef.current != null) return
        if (!container.isConnected || container.offsetWidth === 0) {
          scheduleRetry()
          return
        }

        const widgetId = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          size: 'flexible',
          appearance: 'always',
          callback: (token) => {
            setTurnstileToken((prev) => (prev === token ? prev : token))
          },
          'error-callback': () => console.error('Turnstile verification failed'),
          theme: 'dark',
        })

        widgetIdRef.current = widgetId
        setTurnstileWidgetId(widgetId)
      })
    }

    tryRender()

    return () => {
      cancelled = true
      if (retryTimeoutId != null) {
        window.clearTimeout(retryTimeoutId)
      }
      cleanupWidget()
    }
  }, [enabled, containerEl, mountKey, cleanupWidget, setTurnstileToken, setTurnstileWidgetId])

  useEffect(() => {
    if (!enabled || resetKey === 0 || widgetIdRef.current == null) return

    if (window.turnstile?.reset) {
      window.turnstile.reset(widgetIdRef.current)
    }
    setTurnstileToken(null)
  }, [enabled, resetKey, setTurnstileToken])
}
